/**
 * The transcript, and the one human decision it needs: who each voice belongs to.
 *
 * Transcription services return anonymous labels — "Speaker 1", "Speaker 2" —
 * because they can tell voices apart but not whose they are. Mapping a label to a
 * director is done once per meeting, by a person: SS-1 requires dissent recorded
 * against the dissenting director by name, and a heuristic's confidence is not a
 * good enough basis for putting a name against words that carry that weight.
 *
 * The server does read the transcript for clues — who opened the meeting, who
 * was addressed by name before speaking — and those arrive as SUGGESTIONS, each
 * with its reason. They pre-fill the form so the usual case is "read the reason,
 * press Save", but nothing is stored until somebody presses it.
 *
 * Unmapped labels are shown as themselves rather than hidden. Somebody spoke; the
 * record should say so even while nobody has said who.
 */

import { useEffect, useMemo, useState } from 'react';
import { TbUserCheck, TbAlertTriangle, TbDeviceFloppy, TbSparkles } from 'react-icons/tb';
import Button from '../common/Button';
import { labelClass, inputClass } from '../common/FormControls';

const stamp = (ms) => {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export default function TranscriptPanel({ transcript, saving, onSaveSpeakers }) {
  const { segments = [], speakers = [], attendees = [], suggestions = {} } = transcript || {};

  const [draftMap, setDraftMap] = useState({});

  // Re-seed whenever the server's mapping changes, so a save elsewhere (or a
  // re-transcription) is reflected rather than overwritten by a stale form.
  // A label nobody has named yet is pre-filled with the server's suggestion,
  // which is why the Save button lights up on a fresh transcript: the
  // suggestions are unsaved until a person accepts them.
  useEffect(() => {
    const seeded = {};
    speakers.forEach((s) => { seeded[s.label] = s.value || suggestions[s.label]?.attendeeId || ''; });
    setDraftMap(seeded);
  }, [speakers, suggestions]);

  const dirty = useMemo(
    () => speakers.some((s) => (draftMap[s.label] || '') !== (s.value || '')),
    [speakers, draftMap],
  );

  const unnamed = speakers.filter((s) => !draftMap[s.label]).length;
  // Suggestions still standing — pre-filled, unchanged by the user, not yet saved.
  const suggested = speakers.filter((s) =>
    !s.value && suggestions[s.label] && draftMap[s.label] === suggestions[s.label].attendeeId).length;

  /** An attendee id resolves to a person; anything else is a typed-in name. */
  const displayFor = (value) =>
    attendees.find((a) => String(a.id) === String(value))?.name || value || '';

  // One label for a whole meeting means the provider could not separate voices —
  // not that only one person spoke.
  const singleSpeaker = speakers.length === 1;

  /**
   * A person already used for another label — offered, but flagged.
   *
   * Empty is skipped: two unnamed speakers are not a clash, they are simply two
   * speakers nobody has named yet, and warning about it made the panel shout at
   * a user who had done nothing wrong.
   */
  const takenBy = (value, label) => {
    if (!value) return null;
    return Object.entries(draftMap).find(([l, v]) => v === value && l !== label)?.[0] || null;
  };

  if (!segments.length) return null;

  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[15px] font-bold text-[#2c2a24]">Who said what</h3>
        <span className="text-[11px] text-[#8a8172]">
          {transcript.provider && `${transcript.provider} · `}
          {segments.length} {segments.length === 1 ? 'passage' : 'passages'}
        </span>
      </div>

      {/* The provider's own caveat — shown before anyone relies on the text. */}
      {transcript.speakerCountNotice && !singleSpeaker && (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#b9cfd6] bg-[#f0f6f8] px-3 py-2.5">
          <TbAlertTriangle className="mt-0.5 shrink-0 text-[#2c5560]" size={16} />
          <p className="text-[12px] leading-relaxed text-[#2c5560]">{transcript.speakerCountNotice}</p>
        </div>
      )}

      {transcript.notice && (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#e3c9a0] bg-[#fdf6e9] px-3 py-2.5">
          <TbAlertTriangle className="mt-0.5 shrink-0 text-[#9a5b1e]" size={16} />
          <p className="text-[12px] leading-relaxed text-[#7a5a2a]">{transcript.notice}</p>
        </div>
      )}

      {singleSpeaker ? (
        <div className="mb-4 flex gap-2 rounded-lg border border-[#e3c9a0] bg-[#fdf6e9] px-3 py-2.5">
          <TbAlertTriangle className="mt-0.5 shrink-0 text-[#9a5b1e]" size={16} />
          <p className="text-[12px] leading-relaxed text-[#7a5a2a]">
            This recording came back with a single speaker, so individual voices could not be
            separated. The minutes will be written impersonally and nothing — including any
            dissent — can be attributed to a named director.
          </p>
        </div>
      ) : (
        <p className="mb-3 text-[12px] text-[#6b6252]">
          Match each voice to the director it belongs to. This is done once for the meeting.
          {suggested > 0 && (
            <span className="font-semibold text-[#145f5f]">
              {' '}{suggested} {suggested === 1 ? 'name is' : 'names are'} suggested from the recording — check and save.
            </span>
          )}
          {unnamed > 0 && (
            <span className="font-semibold text-[#8a6d2f]">
              {' '}{unnamed} still unnamed.
            </span>
          )}
        </p>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        {speakers.map((speaker) => {
          const clash = takenBy(draftMap[speaker.label], speaker.label);
          const hint = suggestions[speaker.label];
          const showHint = hint && !speaker.value && draftMap[speaker.label] === hint.attendeeId;
          return (
            <div key={speaker.label}>
              <label className={labelClass} htmlFor={`speaker-${speaker.label}`}>
                {speaker.label}
              </label>
              {/* Type or pick. A plain <select> could only offer the attendance
                  list, but a board meeting is not only its directors — the
                  company secretary, an invitee and the statutory auditor all
                  speak and are minuted "in attendance", and none of them is an
                  attendee. Choosing an attendee stores their id, so the name and
                  DIN follow the register; typing stores the name as given. */}
              <input
                id={`speaker-${speaker.label}`}
                className={inputClass}
                list={`attendees-${speaker.label}`}
                placeholder="Not identified — pick or type a name"
                value={displayFor(draftMap[speaker.label])}
                onChange={(e) => {
                  const typed = e.target.value;
                  // The datalist hands back the visible label, so a value that
                  // matches an attendee's name is stored as that attendee's id
                  // rather than as loose text.
                  const picked = attendees.find((a) => a.name === typed);
                  setDraftMap((m) => ({ ...m, [speaker.label]: picked ? String(picked.id) : typed }));
                }}
              />
              <datalist id={`attendees-${speaker.label}`}>
                {attendees.map((a) => (
                  <option key={a.id} value={a.name}>
                    {a.din ? `DIN ${a.din}` : a.designation || ''}
                  </option>
                ))}
              </datalist>
              {/* Two people cannot be one voice. Allowed, because a mis-set
                  dropdown is fixed by setting the other one — but never silent. */}
              {clash && (
                <p className="mt-1 text-[11px] text-[#8a6d2f]">
                  Also selected for {clash}.
                </p>
              )}
              {/* Why the server thinks so, so the person can agree or not
                  without replaying the audio. */}
              {showHint && !clash && (
                <p className="mt-1 flex items-start gap-1 text-[11px] text-[#145f5f]">
                  <TbSparkles size={12} className="mt-0.5 shrink-0" />
                  <span>Suggested — {hint.reason}. Save to confirm.</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mb-5">
        <Button
          variant="primary"
          size="sm"
          icon={dirty ? TbDeviceFloppy : TbUserCheck}
          loading={saving}
          disabled={!dirty || saving}
          onClick={() => onSaveSpeakers(draftMap)}
        >
          {dirty ? (suggested > 0 && suggested === speakers.filter((s) => !s.value).length ? 'Confirm suggested names' : 'Save names') : 'Names saved'}
        </Button>
      </div>

      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-[#e8e2d5] bg-[#faf8f3] p-3">
        {segments.map((seg, i) => (
          <div key={i} className="mb-3 last:mb-0">
            <div className="mb-0.5 flex items-baseline gap-2">
              <span className="text-[12px] font-bold text-[#145f5f]">{seg.name}</span>
              {seg.din && <span className="text-[10px] text-[#8a8172]">DIN {seg.din}</span>}
              <span className="ml-auto font-mono text-[10px] tabular-nums text-[#a09786]">
                {stamp(seg.startMs)}
              </span>
            </div>
            <p className="text-[13px] leading-relaxed text-[#3a352c]">{seg.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
